import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import BarcodeInput from 'components/field/BarcodeInput';
import Button from 'components/field/Button';
import Alert from 'components/global/Alert';
import Image from 'components/global/Image';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';

import InternationalProforma from 'pages/Shipments/PrintOrders/InternationalProforma';
import PrintAWB from 'pages/Shipments/PrintOrders/PrintAWB';

import { SOUND } from 'services/consts';

import { faPrint } from '@fortawesome/pro-solid-svg-icons';

import * as S from './styled/Packing.styled';

const Packing = ({ completePackingRef }) => {
	const formMethods = useFormContext();
	const { showError } = useAlert();
	const { watch } = formMethods;
	const totalWeight = watch('totalWeight');
	const { t } = useTranslation();
	const globalValue = useRef('');
	const printModal = useModal(PrintAWB);

	const printFunction = () => {
		printModal
			.show({
				ids: [parseInt(watch('orderID'))]
			})
			.then((resolved) => {
				const { internationalOrders = null } = resolved;

				if (internationalOrders?.length > 0) {
					NiceModal.show(InternationalProforma, {
						ids: internationalOrders
					});
				}
			});
	};

	useEffect(() => {
		if (watch('otoPackPackingPageAutomaticPrintAWB')) {
			printFunction();
		}
	}, []);

	useEffect(() => {
		document.onkeydown = (e) => {
			if ((e.which < 65 || e.which > 122) && (e.which < 48 || e.which > 57)) {
				if (e.key === 'Enter') {
					if (
						globalValue.current?.toString() === watch('orderIDShowed')?.toString() ||
						globalValue.current?.toString() === watch('shipmentNumber')
					) {
						printModal.remove();
						formMethods.setValue('validated', true);
						const successSound = new Audio(SOUND.BARCODE_SUCCESS);
						successSound.play();
						completePackingRef.enable();
						setTimeout(() => completePackingRef?.click(), 200);
					} else {
						const errorSound = new Audio(SOUND.BARCODE_ERROR);
						errorSound.play();
						showError();
					}
					globalValue.current = '';
				} else if (e.key === '-' || e.key === '/') {
					globalValue.current = `${globalValue.current}${e.key}`;
				}
				return;
			}
			globalValue.current = globalValue.current + e.key;
		};
		return () => {
			document.onkeydown = null;
		};
	}, [completePackingRef]);

	return (
		<S.ShippingSuccessCard fullWidth rounded padding={5}>
			<S.ShippingSuccessInfoCard rounded>
				<S.InfoContainer>
					<Image url={watch('logoUrl')} />
				</S.InfoContainer>
				<S.Divider />
				<S.InfoContainer>
					<Typography fontWeight="500" color="var(--content-3)">
						shipmentNumber
					</Typography>
					<Button
						component="a"
						href={watch('trackingUrl')}
						label={watch('shipmentNumber')}
						variant="blueBorderless"
						style={{
							padding: 0
						}}
						target="_blank"
						disabledTooltip={watch('trackingUrl')}
					/>
				</S.InfoContainer>
				<S.Divider />
				<S.InfoContainer>
					<Typography fontWeight="500" color="var(--content-3)">
						origin
					</Typography>
					<Typography fontWeight="500">
						{watch('originAddress.cityName')
							? [t(watch('originAddress.cityName')), t(`country.${watch('originAddress.countryName')}`)]
									.filter((item) => !!item)
									.join(', ')
							: [watch('originAddress.city'), watch('originAddress.country')]
									.filter((item) => !!item)
									.join(', ')}
					</Typography>
				</S.InfoContainer>
				<S.Divider />
				<S.InfoContainer>
					<Typography fontWeight="500" color="var(--content-3)">
						destination
					</Typography>
					<Typography fontWeight="500">
						{watch('destinationAddress.cityName')
							? [
									t(watch('destinationAddress.cityName')),
									t(`country.${watch('destinationAddress.countryName')}`)
								]
									.filter((item) => !!item)
									.join(', ')
							: [watch('destinationAddress.city'), watch('destinationAddress.country')]
									.filter((item) => !!item)
									.join(', ')}
					</Typography>
				</S.InfoContainer>
				<S.Divider />
				<S.InfoContainer>
					<Typography fontWeight="500" color="var(--content-3)">
						boxWeight
					</Typography>
					<Typography fontWeight="500">
						{totalWeight} {t('kg')}
					</Typography>
				</S.InfoContainer>
			</S.ShippingSuccessInfoCard>
			<Button icon={faPrint} label="printShippingLabelAWB" onClick={() => printFunction()} />
			<S.BarcodeScanner>
				<Alert variant="warning" showIcon content="packing.scanPrintAwb" />
				<BarcodeInput
					variant="validation"
					validated={watch('validated')}
					onChange={(val) => {
						if (
							(val?.toString() === watch('orderIDShowed')?.toString() ||
								val?.toString() === watch('shipmentNumber')) &&
							!watch('validated')
						) {
							formMethods.setValue('validated', true);
							const successSound = new Audio(SOUND.BARCODE_SUCCESS);
							successSound.play();
							completePackingRef?.click();
						} else {
							const errorSound = new Audio(SOUND.BARCODE_ERROR);
							errorSound.play();
							showError();
						}
					}}
					onEveryChange={(val) => {
						if (val?.toString() === 'printAwbByBarcode') {
							printFunction();
						}
						if (
							completePackingRef &&
							(val?.toString() === watch('orderIDShowed')?.toString() ||
								val?.toString() === watch('shipmentNumber'))
						) {
							completePackingRef.enable();
						} else {
							completePackingRef.disable();
						}
					}}
				/>
			</S.BarcodeScanner>
		</S.ShippingSuccessCard>
	);
};
export default Packing;
